import React, { useEffect } from 'react';
import { toFlag, toPercent } from 'pano-utils';
import { ImageType, LinkTarget, type Logo, LogoAction, LogoType } from '@types';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import ImageContainer from '@components/ImageContainer';
import { Validator } from '@components/Validator';
import Button from '@components/Button';
import Image from '@components/Image';
import Tip from '@components/Tip';
import useTourEditing from '@hooks/useTourEditing';
import { $editingLogo } from '@stores/editingStores';
import Slider from '@components/Slider';
import LinkSetting from '../components/LinkSetting';
import PanelBase from '../PanelBase';
import { selectLibImages } from '../../mediaLib';
import type { PanelBaseProps } from '../types';

const typeItems: DropdownItem<LogoType>[] = [
  { id: LogoType.system, name: '系统默认LOGO' },
  { id: LogoType.custom, name: '自定义LOGO' },
  { id: LogoType.disabled, name: '不显示LOGO' },
];

const actionItems: DropdownItem<LogoAction>[] = [
  { id: LogoAction.none, name: '无操作' },
  { id: LogoAction.link, name: '打开超链接网址' },
];

function LogoBase({ defaultValue, onConfirm }: PanelBaseProps<Logo>) {
  const { state, setState, isChanged, defaultState } = useTourEditing(
    defaultValue,
    {
      type: LogoType.system,
      iconMediaId: 0,
      iconMediaUrl: '',
      scale: 1,
      action: LogoAction.none,
      linkUrl: '',
      openMethod: LinkTarget.blank,
      turnOffBgMusicWhenLayerOpen: 0,
      turnOffBgVoiceWhenLayerOpen: 0,
      openStyle: 1,
    }
  );

  useEffect(() => {
    $editingLogo.api.set(state);
    return () => {
      $editingLogo.api.set(null);
    };
  }, [state]);

  const handleSelectLogo = async () => {
    const { libItems } = await selectLibImages();
    if (!libItems) return;
    setState((draft) => {
      draft.iconMediaId = libItems[0].id;
      draft.iconMediaUrl = libItems[0].url;
    });
  };

  const handleDeleteLogo = () => {
    setState((draft) => {
      draft.iconMediaId = 0;
      draft.iconMediaUrl = '';
    });
  };

  const handleConfirm = () => {
    if (state.type === LogoType.custom) {
      onConfirm(state);
    } else {
      onConfirm({ ...defaultState, type: state.type });
    }
  };

  return (
    <PanelBase
      title={<Title>自定义LOGO</Title>}
      isChanged={isChanged}
      onHelpClick={() => {}}
      onConfirm={handleConfirm}
    >
      <Collapse title="LOGO类型选择">
        <Dropdown
          items={typeItems}
          selected={state.type}
          onChange={(id) =>
            setState((draft) => {
              draft.type = id;
            })
          }
        />
      </Collapse>
      {state.type === LogoType.custom && (
        <>
          <Validator
            value={state.iconMediaUrl}
            validFn={() => !!state.iconMediaUrl}
            warningMessage="请选择LOGO图片"
          >
            {({ value, warning }) => (
              <Collapse title="自定义LOGO">
                <ImageContainer
                  className="h-115"
                  warning={warning}
                  placeholder="请先添加一张图片"
                  onDeleteClick={handleDeleteLogo}
                >
                  {!!value && (
                    <Image
                      src={value}
                      type={ImageType.media}
                      className="max-h-full max-w-full"
                    />
                  )}
                </ImageContainer>
                <div className="mt-10 flex items-center justify-between">
                  <Tip>尺寸不超过：200px</Tip>
                  <Button size="sm" onClick={handleSelectLogo}>
                    添加图片
                  </Button>
                </div>
                {!!value && (
                  <Slider
                    className="mt-20"
                    value={state.scale}
                    min={0.5}
                    max={2}
                    valToString={(val) => toPercent(val, 0)}
                    onChange={(val) => {
                      setState((draft) => {
                        draft.scale = val;
                      });
                    }}
                  >
                    <Slider.Header title="缩放比例" />
                    <Slider.Bar step={0.01} />
                    <Slider.Footer />
                  </Slider>
                )}
              </Collapse>
            )}
          </Validator>
          <Collapse title="LOGO点击操作">
            <Dropdown
              items={actionItems}
              selected={state.action}
              onChange={(id) =>
                setState((draft) => {
                  draft.action = id;
                })
              }
            />
            {state.action === LogoAction.link && (
              <>
                <div className="mt-20">网址：</div>
                <LinkSetting
                  className="mt-10"
                  isRequired
                  value={state.linkUrl}
                  linkTarget={state.openMethod}
                  modalSizeType={state.modalSizeType}
                  size={state.size}
                  openStyle={state.openStyle}
                  isMusicToggleOn={!!state.turnOffBgMusicWhenLayerOpen}
                  onChange={(val) =>
                    setState((draft) => {
                      draft.linkUrl = val;
                    })
                  }
                  onTargetChange={(k) =>
                    setState((draft) => {
                      draft.openMethod = k;
                    })
                  }
                  onMusicToggleChange={(bool) =>
                    setState((draft) => {
                      draft.turnOffBgMusicWhenLayerOpen = toFlag(bool);
                    })
                  }
                  onModalSizeTypeChange={(k) => {
                    setState((draft) => {
                      draft.modalSizeType = k;
                    });
                  }}
                  onModalSizeChange={(val) =>
                    setState((draft) => {
                      draft.size = val;
                    })
                  }
                  onOpenStyleChange={(val) =>
                    setState((draft) => {
                      draft.openStyle = val;
                    })
                  }
                />
              </>
            )}
          </Collapse>
        </>
      )}
    </PanelBase>
  );
}

export default LogoBase;
